// Rankings → Perks → Cancellation fee split (0313; amounts in 0334, Decision Log
// 2026-10-13). It lived in Settings until 2026-10-15; eligibility is decided by
// rank, so the rule belongs with the ranks.
//
// Who receives the after-pickup cancellation fee. Equal split is the default and
// is exactly the rule before 0313. In fixed-amount mode the Super Admin sets a
// therapist amount and a rider amount in PESOS: every eligible therapist on the
// job gets the full therapist amount and every eligible rider the full rider
// amount. Bednbody keeps whatever is not paid out (this replaces 0313's "Bednbody
// keeps none"). One therapist amount + one rider amount may not exceed the
// SMALLEST cancellation fee — the database refuses it, and this card blocks it
// first. If a big group job would pay out more than the fee, the database scales
// every share down evenly so the total never exceeds what was charged. Only the
// ticked ranks receive a share. Superadmin only (the page and the table policy);
// every save is in the audit log.

import { Button } from "@bednbody/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@bednbody/ui/components/card";
import { Checkbox } from "@bednbody/ui/components/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@bednbody/ui/components/tabs";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  type OperatingSettings,
  RANKS,
  type Rank,
  useUpdateOperatingSettings,
} from "./settings.hooks";
import { Field, NumberInput } from "./settingsFields";

const RANK_LABEL: Record<Rank, string> = {
  bronze: "Bronze",
  silver: "Silver",
  gold: "Gold",
  diamond: "Diamond",
};

/** A peso amount: zero or more, at most two decimals — the column is numeric(10,2). */
const validPeso = (n: number) => Number.isFinite(n) && n >= 0 && Math.round(n * 100) === n * 100;

const peso = (n: number) => `₱${n.toLocaleString("en-PH", { maximumFractionDigits: 2 })}`;

const amountString = (n: number | null) => (n === null ? "" : String(n));

function draftFrom(settings: OperatingSettings) {
  return {
    mode: settings.cancellation_share_mode,
    therapist: amountString(settings.cancellation_share_therapist_php),
    rider: amountString(settings.cancellation_share_rider_php),
    therapistRanks: settings.cancellation_share_therapist_ranks,
    riderRanks: settings.cancellation_share_rider_ranks,
  };
}

export function CancellationShareCard({ settings }: { settings: OperatingSettings }) {
  const update = useUpdateOperatingSettings();
  const [draft, setDraft] = useState(() => draftFrom(settings));

  useEffect(() => setDraft(draftFrom(settings)), [settings]);

  // The smallest fee is the ceiling for one therapist + one rider (0334).
  const smallestFee = Math.min(
    settings.cancellation_fee_before_pickup_php,
    settings.cancellation_fee_after_pickup_php,
  );
  const t = Number(draft.therapist);
  const r = Number(draft.rider);
  const amountsGiven = draft.therapist.trim() !== "" && draft.rider.trim() !== "";
  const amountsOk = amountsGiven && validPeso(t) && validPeso(r);
  const overFee = amountsOk && Math.round((t + r) * 100) > Math.round(smallestFee * 100);
  const valid = draft.mode === "equal" || (amountsOk && !overFee);
  const nobodyEligible = draft.therapistRanks.length === 0 && draft.riderRanks.length === 0;

  function toggleRank(role: "therapistRanks" | "riderRanks", rank: Rank, on: boolean) {
    setDraft((d) => ({
      ...d,
      // Kept in rank order so a save reads the same in the audit log however it was ticked.
      [role]: RANKS.filter((x) => (x === rank ? on : d[role].includes(x))),
    }));
  }

  function save() {
    if (draft.mode === "amount" && !amountsOk) {
      toast.error("Enter a peso amount for both therapists and riders (zero is allowed).");
      return;
    }
    if (overFee) {
      toast.error(
        `One therapist amount plus one rider amount can't be more than ${peso(smallestFee)}, the smallest cancellation fee.`,
      );
      return;
    }
    update.mutate(
      {
        cancellation_share_mode: draft.mode,
        // In equal mode, valid amounts are kept so switching back restores them.
        cancellation_share_therapist_php: amountsOk && !overFee ? t : null,
        cancellation_share_rider_php: amountsOk && !overFee ? r : null,
        cancellation_share_therapist_ranks: draft.therapistRanks,
        cancellation_share_rider_ranks: draft.riderRanks,
      },
      {
        onSuccess: () =>
          toast.success("Cancellation fee split saved. It applies to the next cancellation."),
        onError: (error) => toast.error(error.message),
      },
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cancellation fee split</CardTitle>
        <CardDescription>
          Who receives the after-pickup cancellation fee. In fixed-amount mode Bednbody keeps
          whatever is not paid out. A change applies from the next cancellation; shares already paid
          are never recalculated.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <Tabs
          value={draft.mode}
          onValueChange={(value) => {
            if (value === "equal" || value === "amount") {
              setDraft((d) => ({ ...d, mode: value }));
            }
          }}
          className="min-w-0 gap-4"
        >
          <div className="min-w-0 max-w-full overflow-x-auto pb-2">
            <TabsList variant="line" aria-label="How the fee is split" className="w-max">
              <TabsTrigger value="equal">Equal split</TabsTrigger>
              <TabsTrigger value="amount">Fixed amount per person</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="equal">
            <p className="type-body-default text-muted-foreground">
              Every eligible therapist and rider on the job gets the same share of the whole fee.
              Nobody is left out of the fee, and Bednbody keeps none of it.
            </p>
          </TabsContent>
          <TabsContent value="amount">
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Each eligible therapist gets (₱)">
                  <NumberInput
                    step="0.01"
                    value={draft.therapist}
                    onChange={(v) => setDraft((d) => ({ ...d, therapist: v }))}
                  />
                </Field>
                <Field label="Each eligible rider gets (₱)">
                  <NumberInput
                    step="0.01"
                    value={draft.rider}
                    onChange={(v) => setDraft((d) => ({ ...d, rider: v }))}
                  />
                </Field>
              </div>
              {amountsOk ? (
                <p className={overFee ? "type-body-default text-destructive" : "type-body-default text-muted-foreground"}>
                  One therapist and one rider: {peso(t + r)} paid out of the {peso(smallestFee)}{" "}
                  smallest fee
                  {overFee
                    ? " — too much. Lower an amount to save."
                    : `, Bednbody keeps ${peso(smallestFee - t - r)}.`}
                </p>
              ) : (
                <p className="type-body-default text-muted-foreground">
                  The two amounts together can be at most {peso(smallestFee)}, the smallest
                  cancellation fee.
                </p>
              )}
              <p className="type-body-default text-muted-foreground">
                Every eligible person on the job gets the full amount, so a job with more people pays
                out more. If a job would pay out more than the fee collected, everyone's share is
                reduced by the same proportion so the total is never more than the fee.
              </p>
            </div>
          </TabsContent>
        </Tabs>

        <RankRow
          label="Therapist ranks that receive a share"
          selected={draft.therapistRanks}
          onToggle={(rank, on) => toggleRank("therapistRanks", rank, on)}
        />
        <RankRow
          label="Rider ranks that receive a share"
          selected={draft.riderRanks}
          onToggle={(rank, on) => toggleRank("riderRanks", rank, on)}
        />

        <p className="type-body-default text-muted-foreground">
          {draft.mode === "amount"
            ? "Someone whose rank is not ticked gets nothing, and nobody takes their place. If nobody on the job is eligible, nobody is paid and Bednbody keeps the fee."
            : "Someone whose rank is not ticked gets no share — it goes to the eligible staff on the same job. If nobody on the job is eligible, everyone on it splits the fee equally."}
        </p>
        {nobodyEligible ? (
          <p className="type-body-default text-warning">
            {draft.mode === "amount"
              ? "No rank is ticked, so no cancellation will pay anyone."
              : "No rank is ticked, so every cancellation will fall back to an equal split among everyone on the job."}
          </p>
        ) : null}
      </CardContent>
      <CardFooter className="justify-end">
        <Button onClick={save} disabled={update.isPending || !valid}>
          Save changes
        </Button>
      </CardFooter>
    </Card>
  );
}

function RankRow({
  label,
  selected,
  onToggle,
}: {
  label: string;
  selected: readonly Rank[];
  onToggle: (rank: Rank, on: boolean) => void;
}) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-xs font-medium text-muted-foreground">{label}</legend>
      <div className="flex flex-wrap gap-4">
        {RANKS.map((rank) => (
          <label key={rank} className="flex items-center gap-2 type-body-default">
            <Checkbox
              checked={selected.includes(rank)}
              onCheckedChange={(next) => onToggle(rank, next === true)}
            />
            {RANK_LABEL[rank]}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
